const TEST_ATTRIBUTES = ['data-testid', 'data-test', 'data-cy', 'data-qa'];
const MAX_DEPTH = 8;

function escapeIdent(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return value.replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c}`);
}

/** Ids generated by frameworks (React useId, numeric suffixes) change between renders, so skip them. */
function isStableId(id: string): boolean {
  return id.length > 0 && !/^[0-9]/.test(id) && !/[:]/.test(id) && !/\d{4,}/.test(id);
}

function anchorFor(el: Element): string | null {
  for (const attr of TEST_ATTRIBUTES) {
    const value = el.getAttribute(attr);
    if (value) return `[${attr}="${value.replace(/"/g, '\\"')}"]`;
  }
  if (el.id && isStableId(el.id)) return `#${escapeIdent(el.id)}`;
  return null;
}

function positional(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const parent = el.parentElement;
  if (!parent || tag === 'body' || tag === 'html') return tag;
  const sameTag = Array.from(parent.children).filter((c) => c.tagName === el.tagName);
  if (sameTag.length === 1) return tag;
  return `${tag}:nth-of-type(${sameTag.indexOf(el) + 1})`;
}

function isUnique(selector: string, root: ParentNode): boolean {
  try {
    return root.querySelectorAll(selector).length === 1;
  } catch {
    return false;
  }
}

/**
 * Builds a CSS selector that is short and reasonably stable: it prefers test ids and stable ids,
 * and falls back to a tag:nth-of-type path. It skips class names, because utility-class
 * frameworks make those noisy and they change with every restyle.
 */
export function cssSelector(el: Element): string {
  const root = el.ownerDocument;
  const parts: string[] = [];
  let current: Element | null = el;

  while (current && parts.length < MAX_DEPTH) {
    const anchor = anchorFor(current);
    parts.unshift(anchor ?? positional(current));
    const selector = parts.join(' > ');
    if (anchor && isUnique(selector, root)) return selector;
    if (current.tagName === 'BODY' || current.tagName === 'HTML') break;
    current = current.parentElement;
  }
  return parts.join(' > ');
}

/** innerText respects CSS visibility; jsdom and some older engines lack it, so fall back. */
function textOf(el: Element): string {
  const inner = el instanceof HTMLElement ? (el.innerText as string | undefined) : undefined;
  return inner ?? el.textContent ?? '';
}

function snippet(text: string | null | undefined, max = 60): string {
  const clean = (text ?? '').replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

/** Human-readable label for an element, e.g. `button "Save changes"`. */
export function describeElement(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const label =
    el.getAttribute('aria-label') ??
    el.getAttribute('title') ??
    el.getAttribute('placeholder') ??
    el.getAttribute('alt') ??
    textOf(el);
  const text = snippet(label);
  const role = el.getAttribute('role');
  return [role ? `${tag}[role=${role}]` : tag, text ? `"${text}"` : ''].filter(Boolean).join(' ');
}

export function textSnippet(el: Element): string {
  return snippet(textOf(el), 60);
}
